import type { ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { isEndedByDate } from "./personDetailUtils";
import { Tag,Typography } from "../../../../../../../shared/frontend/ui";
import dayjs from "dayjs";
import {
CONTRACT_STATUS_ACTIVE,
CONTRACT_STATUS_CANCELLED,
CONTRACT_STATUS_DRAFT,
CONTRACT_STATUS_ENDED,
CONTRACT_STATUS_PAUSED
} from "../../../app/constants/contractStatus";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly,formatMoney } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { paymentMethodLabel } from "../../../app/utils/paymentMethod";
import type { ColumnDefinition } from "../../../app/utils/useProTableColumnEditor";
import type { ContractRow,ContractTemplateBlockDto,PersonServiceRow } from "./personDetailTypes";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

const STATUS_COLUMN_WIDTH = 120;
const PLAN_NAME_COLUMN_WIDTH = 260;
const TERM_COLUMN_WIDTH = 220;
const BUNDLES_COLUMN_WIDTH = 240;
const SALES_PROMOTIONS_COLUMN_WIDTH = 240;
const START_DATE_COLUMN_WIDTH = 140;
const END_DATE_COLUMN_WIDTH = 140;
const PRICE_COLUMN_WIDTH = 180;
const INSTALLMENT_COUNT_COLUMN_WIDTH = 120;
const INSTALLMENT_AMOUNT_COLUMN_WIDTH = 140;
const PAYMENT_METHOD_COLUMN_WIDTH = 140;
const ALLOWS_ACCESS_COLUMN_WIDTH = 140;

type TextFn = I18nContextValue["text"];

const CONTRACT_INSTALLMENT_FONT_SIZE = "0.85em";

export const PERSON_CONTRACT_COLUMN_DEFINITIONS: ColumnDefinition[] = [
  { key: "status", label: "Status", defaultVisible: true },
  { key: "planName", label: "Contract", defaultVisible: true },
  { key: "term", label: "Contract term", defaultVisible: true },
  { key: "bundles", label: "Service bundles", defaultVisible: false },
  { key: "salesPromotions", label: "Sales promotions", defaultVisible: false },
  { key: "startDate", label: "Start", defaultVisible: true },
  { key: "endDate", label: "End", defaultVisible: true },
  { key: "price", label: "Price", defaultVisible: true },
  { key: "installmentCount", label: "Installments", defaultVisible: false },
  { key: "installmentAmount", label: "Installment", defaultVisible: false },
  { key: "allowsAccess", label: "Access right", defaultVisible: true }
];

type PersonContractColumnsOptions = {
  text: TextFn;
  units: UnitsSettings;
  paymentMethodNameByType: Record<number, string>;
  contractTemplateBlocks: ContractTemplateBlockDto[];
  colorError: string;
};

function isServiceRow(row: ContractRow): boolean {
  return (row as unknown as { sourceContractPackageId?: string | null }).sourceContractPackageId != null;
}

function contractDisplayName(row: ContractRow): string {
  if (isServiceRow(row)) return String((row as unknown as PersonServiceRow).name || "").trim();
  return String(row.planName || "").trim();
}

function findPrimaryService(row: ContractRow): PersonServiceRow | null {
  const subServices = ((row as unknown as { subServices?: PersonServiceRow[] }).subServices || []) as PersonServiceRow[];
  return subServices.find((service) => service.isPrimary === true) || null;
}

function hasRenewal(row: ContractRow): boolean {
  const primaryService = findPrimaryService(row);
  return primaryService?.renewalUnit != null && Number(primaryService.renewalValue || 0) > 0;
}

function getDisplayEndDate(row: ContractRow): string | null {
  if (!isServiceRow(row)) {
    const isCancelled = row.cancellationRequestedDate != null || row.cancellationEffectiveDate != null;
    if (!isCancelled && hasRenewal(row) && row.nextPossibleMembershipEndDate) {
      return String(row.nextPossibleMembershipEndDate).slice(0, ISO_DATE_LENGTH);
    }
    if (row.endDate) return String(row.endDate).slice(0, ISO_DATE_LENGTH);
  }

  const primaryEndDate = findPrimaryService(row)?.endDate;
  if (primaryEndDate) return String(primaryEndDate).slice(0, ISO_DATE_LENGTH);
  if (row.endDate) return String(row.endDate).slice(0, ISO_DATE_LENGTH);
  if (isServiceRow(row)) return null;
  return row.nextPossibleMembershipEndDate ? String(row.nextPossibleMembershipEndDate).slice(0, ISO_DATE_LENGTH) : null;
}

function filterBillableServices(
  services: PersonServiceRow[],
  contractTemplateBlocks: ContractTemplateBlockDto[]
): PersonServiceRow[] {
  return services.filter((service) => {
    const templateBlock = contractTemplateBlocks.find((block) => String(block.id) === String(service.serviceProductId));
    return (templateBlock as { isProportionalCalculationPlaceholder?: boolean } | undefined)
      ?.isProportionalCalculationPlaceholder !== true;
  });
}

function compareDateOnly(leftRaw: string | null | undefined, rightRaw: string | null | undefined): number {
  const left = dayjs(String(leftRaw || "").slice(0, ISO_DATE_LENGTH));
  const right = dayjs(String(rightRaw || "").slice(0, ISO_DATE_LENGTH));
  if (!left.isValid() && !right.isValid()) return 0;
  if (!left.isValid()) return 1;
  if (!right.isValid()) return -1;
  return left.isBefore(right) ? -1 : left.isAfter(right) ? 1 : 0;
}

function renderContractStatus(row: ContractRow, text: TextFn) {
  if (isServiceRow(row)) {
    const serviceRow = row as unknown as PersonServiceRow;
    const endDate = serviceRow.endDate ? String(serviceRow.endDate).slice(0, ISO_DATE_LENGTH) : "";
    const startDate = serviceRow.startDate ? String(serviceRow.startDate).slice(0, ISO_DATE_LENGTH) : "";
    const today = dayjs().format("YYYY-MM-DD");
    const endedByDate = Boolean(endDate && (endDate <= today || endDate === startDate));
    if (endedByDate || serviceRow.isEnded === true) return <Tag color="default">{text("Ended")}</Tag>;
    return <Tag color="green">{text("Active")}</Tag>;
  }

  const status = Number(row.status || 0);
  const endedByDate = isEndedByDate(row);
  if (status === CONTRACT_STATUS_DRAFT) return <Tag color="default">{text("Draft")}</Tag>;
  if (status === CONTRACT_STATUS_ACTIVE && endedByDate) return <Tag color="default">{text("Ended")}</Tag>;
  if (status === CONTRACT_STATUS_ACTIVE) return <Tag color="green">{text("Active")}</Tag>;
  if (status === CONTRACT_STATUS_PAUSED) return <Tag color="red">{text("Locked")}</Tag>;
  if (status === CONTRACT_STATUS_CANCELLED) return <Tag color="red">{text("Cancelled")}</Tag>;
  if (status === CONTRACT_STATUS_ENDED) return <Tag color="default">{text("Ended")}</Tag>;
  return <Tag>{String(row.status ?? "")}</Tag>;
}

function getPrimaryServicePrice(row: ContractRow, contractTemplateBlocks: ContractTemplateBlockDto[]): number | null {
  const subServices = ((row as unknown as { subServices?: PersonServiceRow[] }).subServices || []) as PersonServiceRow[];
  const validServices = filterBillableServices(subServices, contractTemplateBlocks);
  const primary = validServices.find((service) => service.isPrimary === true && service.value != null);
  return primary?.value != null ? Number(primary.value || 0) : null;
}

function getPaymentMethod(row: ContractRow): number | null {
  const primaryService = findPrimaryService(row);
  return primaryService?.paymentMethod != null
    ? Number(primaryService.paymentMethod)
    : row.paymentMethod != null
      ? Number(row.paymentMethod)
      : null;
}

function hasContractAccess(row: ContractRow): boolean {
  const subServices = ((row as unknown as { subServices?: PersonServiceRow[] }).subServices || []) as PersonServiceRow[];
  return subServices.some((service) => service.isActive !== false && service.allowsAccess === true);
}

export function createPersonContractColumns({
  text,
  units,
  paymentMethodNameByType,
  contractTemplateBlocks,
  colorError
}: PersonContractColumnsOptions): Record<string, ListColumn<ContractRow>> {
  return {
    status: {
      title: text("Status"),
      dataIndex: "status",
      key: "status",
      width: STATUS_COLUMN_WIDTH,
      sorter: (a, b) => Number(a.status || 0) - Number(b.status || 0),
      render: (_, row) => renderContractStatus(row, text)
    },
    planName: {
      title: text("Contract"),
      dataIndex: "planName",
      key: "planName",
      width: PLAN_NAME_COLUMN_WIDTH,
      className: "persons-contracts-col-contract",
      ellipsis: false,
      sorter: (a, b) => contractDisplayName(a).localeCompare(contractDisplayName(b)),
      render: (_, row) => {
        const label = contractDisplayName(row) || "\u2013";
        return <span className="persons-contracts-contract-cell-text">{label}</span>;
      }
    },
    term: {
      title: text("Contract term"),
      key: "term",
      width: TERM_COLUMN_WIDTH,
      sorter: (a, b) => String(a.termName || "").localeCompare(String(b.termName || "")),
      render: (_, row) => {
        if (isServiceRow(row) || !row.termName) return "\u2014";
        return row.termName;
      }
    },
    bundles: {
      title: text("Service bundles"),
      key: "bundles",
      width: BUNDLES_COLUMN_WIDTH,
      sorter: (a, b) => (a.bundleNames || []).join(", ").localeCompare((b.bundleNames || []).join(", ")),
      render: (_, row) => isServiceRow(row) || !row.bundleNames?.length ? "\u2014" : row.bundleNames.join(", ")
    },
    salesPromotions: {
      title: text("Sales promotions"),
      key: "salesPromotions",
      width: SALES_PROMOTIONS_COLUMN_WIDTH,
      sorter: (a, b) => (a.salesPromotionNames || []).join(", ").localeCompare((b.salesPromotionNames || []).join(", ")),
      render: (_, row) => isServiceRow(row) || !row.salesPromotionNames?.length ? "\u2014" : row.salesPromotionNames.join(", ")
    },
    startDate: {
      title: text("Start"),
      dataIndex: "startDate",
      key: "startDate",
      width: START_DATE_COLUMN_WIDTH,
      sorter: (a, b) => compareDateOnly(a.startDate, b.startDate),
      render: (_, row) => {
        const raw = isServiceRow(row)
          ? String((row as unknown as PersonServiceRow).startDate || "").slice(0, ISO_DATE_LENGTH)
          : String(row.startDate || "").slice(0, ISO_DATE_LENGTH);
        return raw ? formatDateOnly(raw, units) : "\u2014";
      }
    },
    endDate: {
      title: text("End"),
      dataIndex: "endDate",
      key: "endDate",
      width: END_DATE_COLUMN_WIDTH,
      sorter: (a, b) => {
        const aEndDate = getDisplayEndDate(a);
        const bEndDate = getDisplayEndDate(b);
        return compareDateOnly(aEndDate, bEndDate);
      },
      render: (_, row) => {
        if (isServiceRow(row)) {
          const raw = String((row as unknown as PersonServiceRow).endDate || "").slice(0, ISO_DATE_LENGTH);
          return raw ? formatDateOnly(raw, units) : "\u2013";
        }

        const endRaw = getDisplayEndDate(row) || "";
        if (!endRaw) return "\u2013";
        const formatted = formatDateOnly(endRaw, units);
        const isCancelled = row.cancellationRequestedDate != null || row.cancellationEffectiveDate != null;
        const isFixedTermEnd = !hasRenewal(row) && row.endDate != null;
        return isCancelled || isFixedTermEnd ? <span style={{ color: colorError }}>{formatted}</span> : formatted;
      }
    },
    price: {
      title: text("Price"),
      key: "price",
      width: PRICE_COLUMN_WIDTH,
      sorter: (a, b) =>
        Number(getPrimaryServicePrice(a, contractTemplateBlocks) || 0) -
        Number(getPrimaryServicePrice(b, contractTemplateBlocks) || 0),
      render: (_, row) => {
        if (isServiceRow(row)) {
          const value = (row as unknown as PersonServiceRow).value;
          return value != null ? formatMoney(Number(value || 0), units) : "\u2014";
        }

        const basePrice = getPrimaryServicePrice(row, contractTemplateBlocks);
        const installmentCount = row.paymentPlanInstallmentCount;
        const installmentAmount = row.paymentPlanInstallmentAmount;
        if (installmentCount != null && installmentCount > 1 && installmentAmount != null) {
          return (
            <div>
              <div>{formatMoney(basePrice ?? 0, units)}</div>
              <Typography.Text type="secondary" style={{ fontSize: CONTRACT_INSTALLMENT_FONT_SIZE }}>
                {text("Installments of amount")
                  .replace("{0}", String(installmentCount))
                  .replace("{1}", formatMoney(installmentAmount, units))}
              </Typography.Text>
            </div>
          );
        }

        return basePrice != null ? formatMoney(basePrice, units) : "\u2014";
      }
    },
    installmentCount: {
      title: text("Installments"),
      key: "installmentCount",
      width: INSTALLMENT_COUNT_COLUMN_WIDTH,
      sorter: (a, b) => (a.paymentPlanInstallmentCount ?? 0) - (b.paymentPlanInstallmentCount ?? 0),
      render: (_, row) => {
        const count = row.paymentPlanInstallmentCount;
        return count != null && count > 1 ? `${count} ${text("installments")}` : "\u2014";
      }
    },
    installmentAmount: {
      title: text("Installment"),
      key: "installmentAmount",
      width: INSTALLMENT_AMOUNT_COLUMN_WIDTH,
      sorter: (a, b) => (a.paymentPlanInstallmentAmount ?? 0) - (b.paymentPlanInstallmentAmount ?? 0),
      render: (_, row) => {
        const amount = row.paymentPlanInstallmentAmount;
        const count = row.paymentPlanInstallmentCount;
        return amount != null && count != null && count > 1 ? formatMoney(amount, units) : "\u2014";
      }
    },
    paymentMethod: {
      title: text("Payment method"),
      key: "paymentMethod",
      width: PAYMENT_METHOD_COLUMN_WIDTH,
      sorter: (a, b) => Number(getPaymentMethod(a) || 0) - Number(getPaymentMethod(b) || 0),
      render: (_, row) => {
        if (isServiceRow(row)) {
          const method = (row as unknown as PersonServiceRow).paymentMethod;
          return method == null ? "\u2014" : paymentMethodLabel(Number(method), paymentMethodNameByType);
        }
        const method = getPaymentMethod(row);
        return method == null ? "\u2014" : paymentMethodLabel(method, paymentMethodNameByType);
      }
    },
    allowsAccess: {
      title: text("Access right"),
      key: "allowsAccess",
      width: ALLOWS_ACCESS_COLUMN_WIDTH,
      sorter: (a, b) => Number(hasContractAccess(a)) - Number(hasContractAccess(b)),
      render: (_, row) => {
        const hasAccess = isServiceRow(row)
          ? (row as unknown as PersonServiceRow).allowsAccess === true
          : hasContractAccess(row);
        return hasAccess ? <Tag color="green">{text("Yes")}</Tag> : <Tag>{text("No")}</Tag>;
      }
    }
  };
}
